// src/pages/offlineSales/components/ZReportModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Printer } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { getSessionReport } from '../../../services/api/offlineSales/cashierSessions';
import styles from './OfflineSalesSharedModals.module.css';

const METHOD_LABELS = {
  cash: 'Cash',
  card: 'Card',
  bank_transfer: 'Bank Transfer',
  online: 'Online',
};
const methodLabel = (key) => METHOD_LABELS[key] || key;
// Only worth showing when at least one sale has a real category — an "Other"-only list adds nothing
const hasCategorisedSales = (report) => (report.categorySales || []).some(c => c.categoryId !== null);

const buildPrintHtml = (report, session, formatCurrency) => {
  const sessionData = report.session || session;
  const openedAt = new Date(sessionData.openedAt).toLocaleString();
  const closedAt = sessionData.closedAt ? new Date(sessionData.closedAt).toLocaleString() : null;

  const row = (label, value, bold = false) =>
    `<tr><td>${label}</td><td style="text-align:right">${bold ? `<strong>${value}</strong>` : value}</td></tr>`;

  const section = (title, rows) =>
    `<table><thead><tr><th colspan="2">${title}</th></tr></thead><tbody>${rows}</tbody></table>`;

  let html = `
    <h2>Z-Report — Session #${session.id}</h2>

    ${section('Session Summary',
      row('Opened At', openedAt) +
      (closedAt ? row('Closed At', closedAt) : '') +
      row('Cashier', sessionData.cashier?.name || '—') +
      row('Shop', sessionData.Shop?.name || session.Shop?.name || '—') +
      row('Sales Count', report.totalSalesCount ?? 0) +
      row('Total Sales', formatCurrency(report.totalSalesValue ?? 0), true) +
      row('Store Pickup Payments', formatCurrency(report.storePickupPaymentTotal ?? 0), true)
    )}
  `;

  if (hasCategorisedSales(report)) {
    html += section('Category Wise Sales',
      report.categorySales
        .map(c => row(`${c.name} (${c.count})`, formatCurrency(c.total)))
        .join('')
    );
  }

  if (report.paymentBreakdown && Object.keys(report.paymentBreakdown).length > 0) {
    html += section('Payment Breakdown',
      Object.entries(report.paymentBreakdown)
        .map(([m, a]) => row(methodLabel(m), formatCurrency(a)))
        .join('')
    );
  }

  html += section('Cash Position',
    row('Opening Cash', formatCurrency(sessionData.openingCash ?? 0)) +
    row('Expected in Drawer', formatCurrency(report.cashExpectedInDrawer ?? 0)) +
    (sessionData.closingCash != null ? row('Closing Cash (Counted)', formatCurrency(sessionData.closingCash)) : '')
  );

  if (report.varianceBreakdown && Object.keys(report.varianceBreakdown).length > 0) {
    const varRows = Object.entries(report.varianceBreakdown)
      .map(([m, v]) => {
        const sign = v >= 0 ? '+' : '';
        const color = v === 0 ? 'green' : v > 0 ? 'orange' : 'red';
        return `<tr><td>${methodLabel(m)}</td><td style="text-align:right;color:${color}">${sign}${formatCurrency(v)}</td></tr>`;
      }).join('');
    const tv = report.totalVariance ?? 0;
    const tvColor = tv === 0 ? 'green' : tv > 0 ? 'orange' : 'red';
    html += section('Variance Breakdown',
      varRows +
      `<tr class="total-row"><td><strong>Total Variance</strong></td><td style="text-align:right;color:${tvColor}"><strong>${tv >= 0 ? '+' : ''}${formatCurrency(tv)}</strong></td></tr>`
    );
  }

  html += section('Voids',
    row('Count', report.voidCount ?? 0) +
    row('Total Value', formatCurrency(report.totalVoidValue ?? 0))
  );

  html += section('Returns',
    row('Count', report.returnCount ?? 0) +
    row('Total Value', formatCurrency(report.totalReturnValue ?? 0))
  );

  html += section('Credit Outstanding',
    row('Count', report.creditSalesCount ?? 0) +
    row('Outstanding Balance', formatCurrency(report.totalCreditOutstanding ?? 0))
  );

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Z-Report — Session #${session.id}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Courier New', monospace; font-size: 12px; color: #111; padding: 16px; max-width: 400px; margin: 0 auto; }
    h2 { font-size: 14px; text-align: center; margin-bottom: 12px; border-bottom: 2px solid #111; padding-bottom: 6px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
    thead th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #555; padding: 4px 0; }
    tbody td { padding: 3px 0; vertical-align: top; }
    tr.total-row td { border-top: 1px solid #555; padding-top: 5px; margin-top: 4px; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>${html}</body>
</html>`;
};

const ZReportModal = ({ session, onClose }) => {
  const { t, formatCurrency } = useLocalization();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await getSessionReport(session.id);
        setReport(result);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [session.id]);

  const handlePrint = () => {
    if (!report) return;
    const html = buildPrintHtml(report, session, formatCurrency);
    const win = window.open('', '_blank', 'width=480,height=700');
    win.document.write(html);
    win.document.close();
    win.focus();
    win.onafterprint = () => win.close();
    win.print();
    // Fallback for browsers that don't reliably fire onafterprint on a popup window — long
    // enough not to preempt an in-progress print dialog, just a cleanup net if it never fires.
    setTimeout(() => { try { win.close(); } catch (e) { /* noop */ } }, 60000);
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={`${styles.modal} ${styles.modalLg}`} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h3>{t('offlineSales.zReport')} — #{session.id}</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={styles.cancelBtn} onClick={handlePrint} disabled={!report}>
              <Printer size={14} /> {t('offlineSales.printReceipt')}
            </button>
            <button onClick={onClose} className={styles.closeBtn}><X size={18} /></button>
          </div>
        </div>

        <div className={styles.body}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
              Loading...
            </div>
          ) : !report ? (
            <p>No report data available.</p>
          ) : (
            <>
              {/* ── Overview ── */}
              <div className={styles.reportSection}>
                <h4>{t('offlineSales.zReportTitle')}</h4>
                <div className={styles.reportRow}>
                  <span>{t('offlineSales.openedAt')}</span>
                  <span>{new Date(report.session?.openedAt || session.openedAt).toLocaleString()}</span>
                </div>
                {(report.session?.closedAt || session.closedAt) && (
                  <div className={styles.reportRow}>
                    <span>{t('offlineSales.closedAt')}</span>
                    <span>{new Date(report.session?.closedAt || session.closedAt).toLocaleString()}</span>
                  </div>
                )}
                <div className={styles.reportRow}>
                  <span>{t('offlineSales.salesCount')}</span>
                  <span>{report.totalSalesCount ?? 0}</span>
                </div>
                <div className={styles.reportRowTotal}>
                  <span>{t('offlineSales.totalSales')}</span>
                  <strong>{formatCurrency(report.totalSalesValue ?? 0)}</strong>
                </div>
                <div className={styles.reportRowTotal}>
                  <span>{t('offlineSales.storePickupPayments')}</span>
                  <strong>{formatCurrency(report.storePickupPaymentTotal ?? 0)}</strong>
                </div>
              </div>

              {/* ── Category Wise Sales ── */}
              {hasCategorisedSales(report) && (
                <div className={styles.reportSection}>
                  <h4>{t('offlineSales.categoryWiseSales')}</h4>
                  {report.categorySales.map(c => (
                    <div key={c.categoryId ?? 'other'} className={styles.reportRow}>
                      <span>{c.categoryId === null ? t('offlineSales.categoryOther') : c.name} ({c.count})</span>
                      <span>{formatCurrency(c.total)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Payment Breakdown ── */}
              {report.paymentBreakdown && Object.keys(report.paymentBreakdown).length > 0 && (
                <div className={styles.reportSection}>
                  <h4>{t('offlineSales.paymentBreakdown')}</h4>
                  {Object.entries(report.paymentBreakdown).map(([method, amount]) => (
                    <div key={method} className={styles.reportRow}>
                      <span>{methodLabel(method)}</span>
                      <span>{formatCurrency(amount)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Cash Position ── */}
              <div className={styles.reportSection}>
                <h4>{t('offlineSales.cashExpected')}</h4>
                <div className={styles.reportRow}>
                  <span>{t('offlineSales.openingCash')}</span>
                  <span>{formatCurrency(report.session?.openingCash ?? session.openingCash ?? 0)}</span>
                </div>
                <div className={styles.reportRow}>
                  <span>{t('offlineSales.cashExpected')}</span>
                  <span>{formatCurrency(report.cashExpectedInDrawer ?? 0)}</span>
                </div>
                {(report.session?.closingCash ?? session.closingCash) != null && (
                  <div className={styles.reportRow}>
                    <span>{t('offlineSales.closingCash')}</span>
                    <span>{formatCurrency(report.session?.closingCash ?? session.closingCash)}</span>
                  </div>
                )}
              </div>

              {/* ── Variance Breakdown (only after close) ── */}
              {report.varianceBreakdown && Object.keys(report.varianceBreakdown).length > 0 && (
                <div className={styles.reportSection}>
                  <h4>{t('offlineSales.cashVariance')}</h4>
                  {Object.entries(report.varianceBreakdown).map(([method, variance]) => (
                    <div key={method} className={styles.reportRow}>
                      <span>{methodLabel(method)}</span>
                      <span className={variance === 0 ? styles.varianceGood : styles.variance}>
                        {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
                      </span>
                    </div>
                  ))}
                  <div className={styles.reportRowTotal}>
                    <span>{t('offlineSales.totalVariance')}</span>
                    <strong className={(report.totalVariance ?? 0) === 0 ? styles.varianceGood : styles.variance}>
                      {(report.totalVariance ?? 0) >= 0 ? '+' : ''}{formatCurrency(report.totalVariance ?? 0)}
                    </strong>
                  </div>
                </div>
              )}

              {/* ── Voids ── */}
              <div className={styles.reportSection}>
                <h4>{t('offlineSales.voidSummary')}</h4>
                <div className={styles.reportRow}>
                  <span>{t('common.count', 'Count')}</span>
                  <span>{report.voidCount ?? 0}</span>
                </div>
                <div className={styles.reportRow}>
                  <span>{t('common.total', 'Total')}</span>
                  <span>{formatCurrency(report.totalVoidValue ?? 0)}</span>
                </div>
              </div>

              {/* ── Returns ── */}
              <div className={styles.reportSection}>
                <h4>{t('offlineSales.returnSummary')}</h4>
                <div className={styles.reportRow}>
                  <span>{t('common.count', 'Count')}</span>
                  <span>{report.returnCount ?? 0}</span>
                </div>
                <div className={styles.reportRow}>
                  <span>{t('common.total', 'Total')}</span>
                  <span>{formatCurrency(report.totalReturnValue ?? 0)}</span>
                </div>
              </div>

              {/* ── Credit Outstanding ── */}
              <div className={styles.reportSection}>
                <h4>{t('offlineSales.creditSummary')}</h4>
                <div className={styles.reportRow}>
                  <span>{t('common.count', 'Count')}</span>
                  <span>{report.creditSalesCount ?? 0}</span>
                </div>
                <div className={styles.reportRow}>
                  <span>{t('offlineSales.outstandingBalance')}</span>
                  <span>{formatCurrency(report.totalCreditOutstanding ?? 0)}</span>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button onClick={onClose} className={styles.cancelBtn}>{t('common.close', 'Close')}</button>
        </div>
      </div>
    </div>
  );
};

export default ZReportModal;
